import type {
	FilterRuleModel,
	StatisticsInterpretabilityResponse,
} from '@/shared/generated'

import { pickAll, sum, values } from 'lodash/fp'
import { AlertTriangleIcon, SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSelectedCurrency } from '@/entities/currency'
import { useOptimizationQuery } from '@/entities/optimization'
import { useInterpretabilityStatisticQuery } from '@/entities/optimization-statistic'

import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import ElasticityDistributionChart from './ElasticityDistributionChart'

export default function ElasticityDistributionWidget({
	filters,
}: {
	filters: FilterRuleModel[]
}) {
	const { t } = useTranslation()

	const currency = useSelectedCurrency()
	const optimizationGroupId = useOptimizationGroupId()!
	const optimizationQuery = useOptimizationQuery(optimizationGroupId)
	const interpretabilityStatistic =
		useInterpretabilityStatisticQuery<StatisticsInterpretabilityResponse>(
			{
				optimizations: [
					{
						optimization_group_id: optimizationGroupId,
						optimization_id: optimizationQuery.data!.id,
					},
				],
				filters,
				currency,
			},
			{
				enabled: !!optimizationQuery.data?.id,
			}
		)
	const elasticity = interpretabilityStatistic.data?.elasticity
	if (!elasticity) {
		return null
	}
	const seriesData = elasticity.raw ?? []
	const median = elasticity.median ?? 0
	const totalItems =
		sum(values(pickAll(['high', 'low', 'medium'], median))) ?? 0
	const totalOptimizationItems = optimizationQuery.data?.total_products ?? 0
	if (interpretabilityStatistic.isLoading) {
		return <LoadingPlaceholder />
	}
	if (interpretabilityStatistic.isError) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>
					{interpretabilityStatistic.error.message || t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}

	return (
		<div>
			{seriesData.length > 0 ? (
				<ElasticityDistributionChart data={seriesData} />
			) : (
				<div className='border-base flex max-h-[350px] w-full flex-col overflow-hidden rounded-lg border border-solid py-5'>
					<Empty>
						<EmptyHeader>
							<EmptyMedia variant='icon'>
								<SearchIcon className='text-muted-foreground' />
							</EmptyMedia>
							<EmptyDescription>{t('no_data')}</EmptyDescription>
						</EmptyHeader>
					</Empty>
				</div>
			)}

			<div className='flex flex-col space-y-1'>
				<div className='mt-2 flex justify-between'>
					<span className='text-sm font-medium'>
						{t('products_count_items')}
					</span>
					<span className='text-sm font-medium'>
						{formatNumber(totalOptimizationItems)}
					</span>
				</div>
				<div className='mt-2 flex justify-between'>
					<span className='text-sm font-medium'>{t('general_median')}</span>
					<span className='text-sm font-medium'>{formatNumber(median)}</span>
				</div>
			</div>
			<div className='mt-4 flex flex-col space-y-1'>
				<div className='mt-2 flex justify-between'>
					<span className='text-sm font-medium'>{t('low')}</span>
					<span className='text-sm font-medium'>
						{formatNumber(elasticity.low)} (
						{formatNumber(
							(elasticity.low / totalOptimizationItems) * 100,
							NumberFormats.Percent
						)}
						)
					</span>
				</div>
				<div className='mt-2 flex justify-between'>
					<span className='text-sm font-medium'>{t('high')}</span>
					<span className='text-sm font-medium'>
						{formatNumber(elasticity.high)} (
						{formatNumber(
							(elasticity.high / totalOptimizationItems) * 100,
							NumberFormats.Percent
						)}
						)
					</span>
				</div>
				<div className='mt-2 flex justify-between'>
					<span className='text-sm font-medium'>{t('medium')}</span>
					<span className='text-sm font-medium'>
						{formatNumber(elasticity.medium)} (
						{formatNumber(
							(elasticity.medium / totalOptimizationItems) * 100,
							NumberFormats.Percent
						)}
						)
					</span>
				</div>

				<div className='mt-2 flex justify-between'>
					<Tooltip>
						<TooltipTrigger asChild>
							<span className='text-sm font-medium'>
								{t('n/a', { defaultValue: 'n/a' })}
							</span>
						</TooltipTrigger>
						<TooltipContent>
							{t('elasticity_distribution_na_tooltip')}
						</TooltipContent>
					</Tooltip>
					<span className='text-sm font-medium'>
						{formatNumber(totalOptimizationItems - totalItems)} (
						{formatNumber(
							((totalOptimizationItems - totalItems) / totalOptimizationItems) *
								100,
							NumberFormats.Percent
						)}
						)
					</span>
				</div>
			</div>
		</div>
	)
}

function LoadingPlaceholder() {
	return (
		<div className='flex flex-col items-center space-y-2'>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-20' />
				<Skeleton className='h-5 w-10' />
			</div>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-24' />
				<Skeleton className='h-5 w-12' />
			</div>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-28' />
				<Skeleton className='h-5 w-12' />
			</div>
		</div>
	)
}
